import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Grid as CGrid,
  Input,
  IconButton,
  useToast,
} from "@chakra-ui/react"
import React, { useState, useEffect } from "react"
import { useQuery, useQueryClient } from "react-query"
import {
  fetchProfitLoss,
  fetchScamTrades,
  fetchAllScamTradesForCSV, 
} from "../../../services/main.service"
import _ from "lodash"
import BlockedLoader from "../../composites/blocked-loader"
import Paginator from "../../composites/paginator"
import DateRangePicker, {
  SingleDatePicker,
} from "../../composites/date-range-picker"
import { AiOutlineSearch, AiOutlineDownload } from "react-icons/ai"
import { InputWrapper } from "../../composites"
import { SelectSymbols } from "../../composites/select-symbols"
import { formatDate } from "../../../utils"
import { da } from "date-fns/locale"

// Import the helper function
import downloadJsonToCSVHelper from "../../../utils/downloadJsonToCSVHelper"

// Define interface for transformed data
interface TransformedDealData {
  original: {
    "TIME": string;
    "DEAL": string;
    "BUY/SELL": string;
    "SYMBOL": string;
    "LOGIN": string;
    "PRICE": string;
  }
}

const ScamTradesPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [viewDealIndex, setViewDealIndex] = useState<number | null>(null)
  const [amt, setAmt] = useState("1")
  const [perPage, setPerPage] = useState(30)
  const [totalPage, setTotalPages] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()

  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState<{
    year: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
  } = useQuery(
    [
      "scam-trades",
      {
        finalState,
        currentPage,
      },
    ],
    () =>
      fetchScamTrades(
        finalState.year,
        currentPage,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr
      ),
    {
      refetchInterval: 4000,
    }
  )

  useEffect(() => {
    console.log("CURRENT PAGE CHANGE" + currentPage)
  }, [currentPage])

  function paginateArray<T>(
    array: T[],
    currentPage: number,
    perPage: number
  ): T[] {
    const startIndex = (currentPage - 1) * perPage
    const endIndex = startIndex + perPage

    return array.slice(startIndex, endIndex)
  }

const downloadCSV = async () => {
  try {
    setIsDownloading(true);
    
    // Fetch all data for CSV with the current filter parameters
    const response = await fetchAllScamTradesForCSV(
      finalState.year,
      finalState?.startDate ? formatDate(finalState?.startDate) : "",
      finalState?.endDate
        ? formatDate(finalState.endDate)
        : finalState?.startDate
        ? formatDate(finalState?.startDate)
        : "",
      finalState?.loginId,
      finalState?.symbolStr
    );
    
    // Check if we have data
    if (!response?.data?.scam_trades?.data || response.data.scam_trades.data.length === 0) {
      toast({
        title: "No data available",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      setIsDownloading(false);
      return;
    }
    
    // Define the columns for the CSV
    const columns = ["TIME", "DEAL", "BUY/SELL", "SYMBOL", "LOGIN", "PRICE"];
    
    // Transform the data into a format suitable for the CSV helper
    const transformedData: TransformedDealData[] = [];
    
    _.forEach(response.data.scam_trades.data, (d) => {
      _.forEach(d?.data, (_d) => {
        const dealObj: TransformedDealData = {
          original: {
            "TIME": _d[2] ?? "-",
            "DEAL": _d[0] ?? "-",
            "BUY/SELL": _d[1] === 0 ? "BUY" : "SELL",
            "SYMBOL": _d[3] ?? "-",
            "LOGIN": _d[4] ?? "-",
            "PRICE": Math.round(_d[5]).toFixed(2) ?? "-"
          }
        };
        transformedData.push(dealObj);
      });
      

      transformedData.push({
        original: {
          "TIME": "-", 
          "DEAL": "-", 
          "BUY/SELL": "-", 
          "SYMBOL": "-", 
          "LOGIN": "-", 
          "PRICE": "-"
        }
      });
    });
    

    const csvContent = convertToCsv(transformedData, columns);
    
    // Now trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `scam-trades-${finalState.year}-${formatDate(finalState.startDate || new Date())}.csv`);
    document.body.appendChild(link); 
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download successful",
      description: `${transformedData.length} records downloaded`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error("Error downloading CSV:", error);
    toast({
      title: "Download failed",
      description: "There was an error downloading the CSV file",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setIsDownloading(false);
  }
};


const convertToCsv = (data: TransformedDealData[], columns: string[]) => {

  let csvContent = columns.join(',') + '\n';
  

  data.forEach(item => {
    const row = columns.map(column => {
      const value = item.original[column as keyof typeof item.original] || '';
      

      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvContent += row.join(',') + '\n';
  });
  
  return csvContent;
};

  return (
    <>
      <CGrid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(_option: string) => {
              setSymbolStr(_option)
            }}
          />
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  updateFinalState()
                }}
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>

            <Box pt={[0, "35px"]}>
              <Button
                leftIcon={<AiOutlineDownload />}
                colorScheme="green"
                isDisabled={isLoading || isDownloading}
                isLoading={isDownloading}
                loadingText="Downloading..."
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
            </Box>
          </Flex>
        </Box>
      </CGrid>
      <Box>
        <Grid>
          {isLoading ? (
            <Tr>
              <Td colSpan={captions?.length}>
                <BlockedLoader isBlocked={isLoading}>
                  <></>
                </BlockedLoader>
              </Td>
            </Tr>
          ) : (
            <>
              {data?.data?.length ? (
                <>
                  {_.map(data?.data, (deal, index: number) => (
                    <GridRow
                      deal={deal}
                      onViewDeals={() => {
                        setViewDealIndex(index)
                      }}
                    />
                  ))}
                </>
              ) : (
                <>
                  <Tr>
                    <Td colSpan={captions?.length}>
                      <Box p={4} textAlign="center">
                        <Text color="text.regular">
                          No data found for the selected dates
                        </Text>
                      </Box>
                    </Td>
                  </Tr>
                </>
              )}
            </>
          )}
        </Grid>
      </Box>
      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={data?.last_page ?? 1}
          onChangePage={(_p: number) => {
            setCurrentpage(_p)
            console.log("SRTTING VUTTERNYT PAHe", _p)
          }}
        />
      </Box>
      {typeof viewDealIndex === "number" ? (
        <ViewDealsModal
          onClose={() => setViewDealIndex(null)}
          _dealsObj={
            data?.data[viewDealIndex]
              ? data?.data[viewDealIndex]?.data
              : undefined
          }
        />
      ) : (
        ""
      )}
    </>
  )
}

export default ScamTradesPage

const captions = ["Time", "Entry", "Action", "Symbol", "Total Trades", "-"]

const Grid = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

const GridRow = ({
  deal,
  onViewDeals,
}: {
  deal: any
  onViewDeals: () => void
}) => {
  const renderPofitLoss = (_amount: number) => {
    return (
      <Text
        color={_amount < 0 ? "red" : "green"}
        fontSize="md"
        fontWeight="bold"
        minWidth="100%"
        textAlign="right"
      >
        {_amount}
      </Text>
    )
  }

  return (
    <>
      <Tr>
        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.t ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.e ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.a ?? "-"}
            </Text>
          </Flex>
        </Td>

        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.s ?? "-"}
            </Text>
          </Flex>
        </Td>

        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.totalTrade ?? "-"}
            </Text>
          </Flex>
        </Td>

        <Td>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              <Button size="sm" colorScheme="blue" onClick={onViewDeals}>
                View Deals
              </Button>
            </Text>
          </Flex>
        </Td>
      </Tr>
    </>
  )
}

const ViewDealsModal = ({
  onClose,
  _dealsObj,
}: {
  onClose: () => void
  _dealsObj: any
}) => {
  const dealsCaptions: any[] = [
    "Deal",
    "Order",
    "Position",
    "Login",
    "Name",
    "Time",
    "Type",
    "Entry",
    "Symbol",
    "Volume",
    "Price",
    "S / L",
    "T / P",
    "Reason",
    "Commission",
    "Fee",
    "Profit",
    "Dealer",
    "Comment",
  ]
  return (
    <>
      <Modal isOpen={true} onClose={onClose} isCentered size="7xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>View Scam Deals</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box overflow="auto" height="100%" maxHeight="70vh">
              <Table variant="striped" color="text.regular" border={1}>
                <Thead>
                  <Tr my=".8rem" ps="0px">
                    {dealsCaptions.map((caption, idx) => {
                      return (
                        <Th
                          color="gray.400"
                          key={idx}
                          ps={idx === 0 ? "0px" : "0px"}
                          textAlign={caption === "PL" ? "right" : "unset"}
                        >
                          {caption}
                        </Th>
                      )
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {_.isEmpty(_dealsObj) ? (
                    <Tr>
                      <Td colSpan={dealsCaptions?.length}>
                        <Box p={4} textAlign="center">
                          <Text color="text.regular">No deals found</Text>
                        </Box>
                      </Td>
                    </Tr>
                  ) : (
                    _.map(_dealsObj, (deal) => (
                      <>
                        <Tr>
                          {_.map(deal, (_d, index) => {
                            if (deal?.length === index + 1) return <></>

                            return (
                              <Td pl={1} pr={2}>
                                <Flex
                                  align="center"
                                  py=".8rem"
                                  minWidth="100%"
                                  flexWrap="nowrap"
                                >
                                  <Text
                                    fontSize="md"
                                    fontWeight="bold"
                                    minWidth="100%"
                                  >
                                    {_d}
                                  </Text>
                                </Flex>
                              </Td>
                            )
                          })}
                        </Tr>
                      </>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}